import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PhotoUpload from './PhotoUpload';
import { COLORS, GAS_DEFAULTS } from '../utils/constants';

const { FiSave } = FiIcons;

const ReadingInput = ({ meter, onAddReading }) => {
  const now = new Date();
  const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
  
  const [datetime, setDatetime] = useState(localDateTime);
  const [value, setValue] = useState('');
  const [photo, setPhoto] = useState(null);
  
  // Gas-specific fields
  const [gasZ, setGasZ] = useState(meter.params?.zNumber || GAS_DEFAULTS.zNumber);
  const [gasHeat, setGasHeat] = useState(meter.params?.heatingValue || GAS_DEFAULTS.heatingValue);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const normalizedValue = value.replace(',', '.');
    
    if (!normalizedValue || isNaN(parseFloat(normalizedValue))) {
      alert('Bitte geben Sie einen gültigen Zählerstand ein.');
      return;
    }

    const reading = {
      timestamp: new Date(datetime).toISOString(),
      value: parseFloat(normalizedValue),
      photo: photo || undefined
    };

    // Add gas params if this is a gas meter
    if (meter.type === 'gas') {
      if (!gasZ || gasZ === 0 || !gasHeat || gasHeat === 0) {
        alert('Bitte tragen Sie Zustandszahl und Brennwert ein (finden Sie auf Ihrer Gasrechnung).');
        return;
      }
      reading.gasParams = {
        zNumber: gasZ,
        heatingValue: gasHeat
      };
    }

    onAddReading(reading);
    
    // Reset form
    setValue('');
    setPhoto(null);
    const newNow = new Date();
    const newLocalDateTime = new Date(newNow.getTime() - newNow.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setDatetime(newLocalDateTime);
  };

  const isGas = meter.type === 'gas';
  const isWater = meter.type === 'water' || meter.type === 'waterCold' || meter.type === 'waterWarm';
  
  const getDecimalPlaces = () => {
    if (meter.type === 'electricity') return 1;
    if (isGas || isWater) return 3;
    return 2;
  };

  const getPlaceholder = () => {
    const places = getDecimalPlaces();
    if (places === 1) return "z.B. 123,4";
    if (places === 3) return "z.B. 123,456";
    return "z.B. 123,45";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-6"
    >
      <h3 className="text-lg font-bold mb-4" style={{ color: COLORS.primary }}>
        Neue Ablesung erfassen
      </h3>

      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Photo Upload */}
        <PhotoUpload photoPreview={photo} onPhotoSelect={setPhoto} />

        {/* Date & Time */}
        <div>
          <label className="block text-sm font-bold text-gray-600 mb-2">
            Datum & Uhrzeit der Ablesung
          </label>
          <input
            type="datetime-local"
            value={datetime}
            onChange={(e) => setDatetime(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-500 focus:outline-none transition-colors"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            💡 Sie können auch alte Ablesungen nachtragen - sie werden automatisch richtig nach Datum/Zeit einsortiert
          </p>
        </div>

        {/* Reading Value */}
        <div>
          <label className="block text-sm font-bold text-gray-600 mb-2">
            Zählerstand {isGas || isWater ? '(m³)' : '(kWh)'}
          </label>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={getPlaceholder()}
            className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-500 focus:outline-none transition-colors text-lg font-mono"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            {meter.type === 'electricity' && '⚡ Strom: 1 Nachkommastelle (z.B. 123,4 kWh)'}
            {isGas && '🔥 Gas: 3 Nachkommastellen (z.B. 123,456 m³)'}
            {isWater && '💧 Wasser: 3 Nachkommastellen (z.B. 123,456 m³)'}
          </p>
        </div>

        {/* Gas-specific fields */}
        {isGas && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="p-4 rounded-lg border-2"
            style={{ borderColor: COLORS.gas, backgroundColor: `${COLORS.gas}15` }}
          >
            <h4 className="text-sm font-bold mb-3" style={{ color: COLORS.gas }}>
              Gas-Umrechnung (für kWh-Berechnung)
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">
                  Zustandszahl (z-Zahl)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  value={gasZ}
                  onChange={(e) => setGasZ(parseFloat(e.target.value))}
                  className="w-full px-3 py-2 rounded border focus:outline-none focus:ring-2"
                  style={{ borderColor: COLORS.gas }}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Steht auf Ihrer Gasrechnung</p>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">
                  Brennwert (kWh/m³)
                </label>
                <input
                  type="number"
                  step="0.001"
                  value={gasHeat}
                  onChange={(e) => setGasHeat(parseFloat(e.target.value))}
                  className="w-full px-3 py-2 rounded border focus:outline-none focus:ring-2"
                  style={{ borderColor: COLORS.gas }}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Steht auf Ihrer Gasrechnung</p>
              </div>
            </div>
            <div className="mt-3 p-2 bg-white rounded text-xs">
              <strong>Formel:</strong> kWh = (m³ neu - m³ alt) × Zustandszahl × Brennwert
            </div>
          </motion.div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full py-4 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 text-lg"
          style={{ backgroundColor: COLORS.secondary }}
        >
          <SafeIcon icon={FiSave} size={20} />
          Ablesung speichern
        </button>
      </form>
    </motion.div>
  );
};

export default ReadingInput;